import { useCallback, useEffect, useRef } from 'react'

/**
 * The gesture ladder from 03-ui-spec.md section 2.4.
 *
 * Scrolling and range-selection share one surface, so the two have to be told
 * apart without either ever feeling ambiguous:
 *
 *   held < 120ms, released still     -> tap, select one block
 *   held < 120ms, moved vertically   -> scroll
 *   held >= 120ms, then moved        -> drag-select a range
 *   held >= 280ms, still             -> sticky multi-select
 *   held >= 500ms, still             -> context menu
 *
 * TWO THINGS THAT DO NOT WORK, both of which look obvious:
 *
 *   Flipping `touch-action` to 'none' when the drag arms. The value is latched
 *   by the compositor at pointer-down; changing it mid-gesture has no effect on
 *   the touch already in progress.
 *
 *   Calling setPointerCapture to stop the scroll. That retargets EVENTS; it
 *   does not suppress native scrolling.
 *
 * What does work is a non-passive `touchmove` listener that calls
 * preventDefault() while a drag is armed. It must be registered non-passive
 * FROM THE OUTSET -- passivity cannot be changed after registration either.
 * This works because the 120ms rung requires a stationary finger, so scrolling
 * has not begun and the event is still cancelable.
 */

export interface GestureCallbacks {
  /** Which row is under this vertical position, in content coordinates. */
  rowAtClientY: (clientY: number) => number | null
  onTap: (index: number) => void
  onDragStart: (index: number) => void
  onDragMove: (index: number) => void
  onDragEnd: () => void
  onStickySelect: (index: number) => void
  onContextMenu: (index: number, clientX: number, clientY: number) => void
}

export const DRAG_ARM_MS = 120
export const STICKY_MS = 280
export const CONTEXT_MENU_MS = 500
/** Movement beyond this before the drag arms means the user is scrolling. */
export const MOVE_TOLERANCE_PX = 8
/** Distance from an edge at which auto-scroll begins. */
export const EDGE_ZONE_PX = 60
export const MAX_AUTO_SCROLL_PX_PER_SEC = 800

type Phase = 'idle' | 'pending' | 'scrolling' | 'dragging' | 'consumed'

export function useBlockGestures(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  callbacks: GestureCallbacks,
) {
  // Held in a ref so the handlers below stay stable across renders while still
  // calling the latest callbacks. Updated in an effect, never during render.
  const cb = useRef(callbacks)
  useEffect(() => {
    cb.current = callbacks
  }, [callbacks])

  const phase = useRef<Phase>('idle')
  const pointerId = useRef<number | null>(null)
  const startY = useRef(0)
  const startX = useRef(0)
  const startedAt = useRef(0)
  const lastClientY = useRef(0)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const autoScrollFrame = useRef<number | null>(null)

  const clearTimers = useCallback(() => {
    for (const t of timers.current) clearTimeout(t)
    timers.current = []
  }, [])

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrame.current !== null) {
      cancelAnimationFrame(autoScrollFrame.current)
      autoScrollFrame.current = null
    }
  }, [])

  const reset = useCallback(() => {
    clearTimers()
    stopAutoScroll()
    phase.current = 'idle'
    pointerId.current = null
  }, [clearTimers, stopAutoScroll])

  /**
   * Scrolls while the finger sits near an edge, so a drag can run past the
   * viewport without the user having to let go.
   *
   * The loop is a function DECLARATION inside the callback so it can schedule
   * itself; a self-referencing `const` arrow inside useCallback is flagged as
   * being used before declaration.
   */
  const startAutoScroll = useCallback(() => {
    if (autoScrollFrame.current !== null) return

    function step() {
      const el = scrollRef.current
      if (!el || phase.current !== 'dragging') {
        stopAutoScroll()
        return
      }

      const rect = el.getBoundingClientRect()
      const y = lastClientY.current

      let velocity = 0
      if (y < rect.top + EDGE_ZONE_PX) {
        const depth = (rect.top + EDGE_ZONE_PX - y) / EDGE_ZONE_PX
        velocity = -Math.min(1, depth) * MAX_AUTO_SCROLL_PX_PER_SEC
      } else if (y > rect.bottom - EDGE_ZONE_PX) {
        const depth = (y - (rect.bottom - EDGE_ZONE_PX)) / EDGE_ZONE_PX
        velocity = Math.min(1, depth) * MAX_AUTO_SCROLL_PX_PER_SEC
      }

      if (velocity !== 0) {
        el.scrollTop += velocity / 60
        const index = cb.current.rowAtClientY(y)
        if (index !== null) cb.current.onDragMove(index)
      }

      autoScrollFrame.current = requestAnimationFrame(step)
    }

    autoScrollFrame.current = requestAnimationFrame(step)
  }, [scrollRef, stopAutoScroll])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Ignore every pointer after the first. A second finger arriving during
      // a drag must not extend the selection.
      if (pointerId.current !== null) return
      if (e.button !== 0 && e.pointerType === 'mouse') return

      pointerId.current = e.pointerId
      phase.current = 'pending'
      startY.current = e.clientY
      startX.current = e.clientX
      lastClientY.current = e.clientY
      startedAt.current = performance.now()

      const index = cb.current.rowAtClientY(e.clientY)
      if (index === null) return

      clearTimers()

      timers.current.push(
        setTimeout(() => {
          if (phase.current !== 'pending') return
          cb.current.onStickySelect(index)
        }, STICKY_MS),
      )

      timers.current.push(
        setTimeout(() => {
          if (phase.current !== 'pending') return
          phase.current = 'consumed'
          cb.current.onContextMenu(index, startX.current, startY.current)
        }, CONTEXT_MENU_MS),
      )
    },
    [clearTimers],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current !== e.pointerId) return
      lastClientY.current = e.clientY

      if (phase.current === 'dragging') {
        const index = cb.current.rowAtClientY(e.clientY)
        if (index !== null) cb.current.onDragMove(index)
        return
      }

      if (phase.current !== 'pending') return

      const moved = Math.abs(e.clientY - startY.current) + Math.abs(e.clientX - startX.current)
      if (moved < MOVE_TOLERANCE_PX) return

      const heldFor = performance.now() - startedAt.current

      if (heldFor < DRAG_ARM_MS) {
        // Moved too soon: this is a scroll. Stand down entirely, including the
        // sticky and context-menu timers.
        phase.current = 'scrolling'
        clearTimers()
        return
      }

      // Held, then moved: a range selection.
      phase.current = 'dragging'
      clearTimers()

      const index = cb.current.rowAtClientY(startY.current)
      if (index !== null) cb.current.onDragStart(index)

      const current = cb.current.rowAtClientY(e.clientY)
      if (current !== null) cb.current.onDragMove(current)

      startAutoScroll()
    },
    [clearTimers, startAutoScroll],
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current !== e.pointerId) return

      const wasDragging = phase.current === 'dragging'
      const wasPending = phase.current === 'pending'
      const heldFor = performance.now() - startedAt.current
      const moved = Math.abs(e.clientY - startY.current) + Math.abs(e.clientX - startX.current)

      clearTimers()
      stopAutoScroll()

      if (wasDragging) {
        cb.current.onDragEnd()
      } else if (wasPending && moved < MOVE_TOLERANCE_PX && heldFor < STICKY_MS) {
        const index = cb.current.rowAtClientY(e.clientY)
        if (index !== null) cb.current.onTap(index)
      }

      phase.current = 'idle'
      pointerId.current = null
    },
    [clearTimers, stopAutoScroll],
  )

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current !== e.pointerId) return
      // iOS fires this when a system gesture takes over -- Control Centre, a
      // notification, an incoming call. Treat it as abandoned, never completed.
      if (phase.current === 'dragging') cb.current.onDragEnd()
      reset()
    },
    [reset],
  )

  /**
   * The non-passive listener that actually suppresses scrolling.
   *
   * Registered once, non-passive, for the lifetime of the component. It cannot
   * be added later: passivity is fixed at registration, just as touch-action is
   * fixed at pointer-down.
   */
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const onTouchMove = (event: TouchEvent) => {
      if (phase.current !== 'dragging') return
      // Once a scroll is under way Safari reports cancelable === false, and
      // nothing can stop it -- which is correct, the user is scrolling.
      if (event.cancelable) event.preventDefault()
    }

    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [scrollRef])

  useEffect(() => reset, [reset])

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    /** Safari's own long-press menu would otherwise fight ours. */
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  }
}
